import { registerSW } from 'virtual:pwa-register'
import { isMobileDevice } from '@/utils/mobileDetection'
import { checkIfPWAInstalled } from '@/utils/pwaDetection'

export default {
  install(app) {
    // Registrar PWA en todos los dispositivos, pero con configuraciones diferentes
    const isMobile = isMobileDevice()
    console.log(`PWA: Dispositivo ${isMobile ? 'móvil' : 'desktop'} detectado`)
    
    // Configuración automática de caché para todos los dispositivos
    const pwaConfig = {
      onNeedRefresh() {
        // En desktop, actualizar automáticamente
        if (!isMobile) {
          console.log('🔄 Actualización automática en desktop')
          window.location.reload()
        } else {
          app.config.globalProperties.$eventBus?.emit('pwa-update-available')
        }
      },
      onOfflineReady() {
        app.config.globalProperties.$eventBus?.emit('pwa-offline-ready')
      },
      onRegistered(swRegistration) {
        console.log('Service Worker registrado:', swRegistration)
        
        // Limpiar caché automáticamente al registrar
        if (swRegistration.active) {
          clearOldCaches()
        }
        
        // Configurar limpieza automática periódica
        setupAutomaticCacheCleanup()
      },
      onRegisterError(error) {
        console.error('Error al registrar Service Worker:', error)
      }
    }
    
    // Función para limpiar caches antiguos
    const clearOldCaches = async () => {
      try {
        if ('caches' in window) {
          const cacheNames = await caches.keys()
          const oldCaches = cacheNames.filter(name => 
            name.includes('vite-plugin-pwa') || 
            name.includes('workbox') ||
            name.includes('static-resources') ||
            name.includes('api-cache')
          )
          
          await Promise.all(
            oldCaches.map(cacheName => {
              console.log('🗑️ Limpiando cache antiguo:', cacheName)
              return caches.delete(cacheName)
            })
          )
        }
      } catch (error) {
        console.error('Error limpiando caches:', error)
      }
    }
    
    // Configurar limpieza automática de caché
    const setupAutomaticCacheCleanup = () => {
      // Limpiar caché cada 6 horas en desktop, 24 horas en móvil
      const cleanupInterval = isMobile ? 24 * 60 * 60 * 1000 : 6 * 60 * 60 * 1000
      
      setInterval(() => {
        console.log('🧹 Limpieza automática de caché')
        clearOldCaches()
        
        // También limpiar localStorage y sessionStorage periódicamente
        if (!isMobile) {
          const keysToKeep = ['auth', 'user', 'preferences'] // Mantener datos importantes
          const currentKeys = Object.keys(localStorage)
          
          currentKeys.forEach(key => {
            if (!keysToKeep.includes(key)) {
              localStorage.removeItem(key)
            }
          })
        }
      }, cleanupInterval)
    }
    
    // Registrar el service worker con configuración automática
    const updateSW = registerSW(pwaConfig)

    // Agregar métodos globales para PWA
    app.config.globalProperties.$pwa = {
      update: updateSW,
      isInstalled: () => checkIfPWAInstalled(),
      isOnline: () => navigator.onLine,
      isMobile: () => isMobileDevice(),
      install: () => {
        // Trigger install prompt
        const event = new Event('beforeinstallprompt')
        window.dispatchEvent(event)
      }
    }

    // Event bus para comunicación entre componentes
    if (!app.config.globalProperties.$eventBus) {
      app.config.globalProperties.$eventBus = {
        listeners: {},
        emit(event, data) {
          if (this.listeners[event]) {
            this.listeners[event].forEach(callback => callback(data))
          }
        },
        on(event, callback) {
          if (!this.listeners[event]) {
            this.listeners[event] = []
          }
          this.listeners[event].push(callback)
        },
        off(event, callback) {
          if (this.listeners[event]) {
            this.listeners[event] = this.listeners[event].filter(cb => cb !== callback)
          }
        }
      }
    }

    // Agregar directiva para detectar instalación
    app.directive('pwa-install', {
      mounted(el, binding) {
        // Funcionar en todos los dispositivos
        const isMobile = isMobileDevice()
        
        const checkInstallation = () => {
          const isInstalled = checkIfPWAInstalled()
          if (binding.value) {
            binding.value(isInstalled)
          }
        }

        // Verificar al montar
        checkInstallation()

        // Escuchar cambios en el modo de visualización
        if (window.matchMedia) {
          window.matchMedia('(display-mode: standalone)').addEventListener('change', checkInstallation)
        }

        // Escuchar evento de instalación
        window.addEventListener('appinstalled', checkInstallation)
      },
      unmounted(el, binding) {
        if (window.matchMedia) {
          window.matchMedia('(display-mode: standalone)').removeEventListener('change', binding.value)
        }
        window.removeEventListener('appinstalled', binding.value)
      }
    })

    // Agregar directiva para detectar estado online/offline
    app.directive('online-status', {
      mounted(el, binding) {
        // Funcionar en todos los dispositivos
        const isMobile = isMobileDevice()
        
        const updateStatus = () => {
          const isOnline = navigator.onLine
          if (binding.value) {
            binding.value(isOnline)
          }
        }

        // Verificar al montar
        updateStatus()

        // Escuchar cambios de conectividad
        window.addEventListener('online', updateStatus)
        window.addEventListener('offline', updateStatus)
      },
      unmounted(el, binding) {
        window.removeEventListener('online', binding.value)
        window.removeEventListener('offline', binding.value)
      }
    })
  }
} 